import { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';

const TODAY = new Date().toISOString().slice(0, 10);

const emptyFixed = {
    outward_date:           TODAY,
    invoice_challan_number: '',
    destination:            '',
    destination_type:       '',
    updated_by:             '',
};

const emptyRow = { ean_number:'', product_name:'', quantity:'', mfg_date:'', exp_date:'', skuSearch:'', skuDropdown:false, skuHighlight:0, skuPreview:'' };

export default function OutwardForm({ user }) {
    const [mode,          setMode]          = useState('single');
    const [fixed,         setFixed]         = useState({ ...emptyFixed, updated_by: user?.username || '' });
    const [rows,          setRows]          = useState([{ ...emptyRow }]);
    const [skus,          setSkus]          = useState([]);
    const [destTypes,     setDestTypes]     = useState([]);
    const [destinations,  setDestinations]  = useState([]);
    const [recent,        setRecent]        = useState([]);
    const [loading,       setLoading]       = useState(false);
    const [submitting,    setSubmitting]    = useState(false);
    const [msg,           setMsg]           = useState(null);
    const [editBatch,     setEditBatch]     = useState(null);
    const [deleteKey,     setDeleteKey]     = useState(null);
    const [expandedBatch, setExpandedBatch] = useState(null);
    const [batchDetails,  setBatchDetails]  = useState({});

    // Single mode
    const [singleForm,    setSingleForm]    = useState({ ...emptyFixed, updated_by: user?.username || '', ean_number:'', quantity:'', mfg_date:'', exp_date:'' });
    const [selSku,        setSelSku]        = useState(null);
    const [skuSearch,     setSkuSearch]     = useState('');
    const [skuDropdown,   setSkuDropdown]   = useState(false);
    const [skuHighlight,  setSkuHighlight]  = useState(0);
    const [skuPreview,    setSkuPreview]    = useState('');
    const [editKey,       setEditKey]       = useState(null);

    // PO/SO mode
    const [poSearch,       setPoSearch]       = useState('');
    const [poSearchType,   setPoSearchType]   = useState('so');
    const [poData,         setPoData]         = useState(null);
    const [poLoading,      setPoLoading]      = useState(false);
    const [poError,        setPoError]        = useState('');
    const [poChecked,      setPoChecked]      = useState({});
    const [poDispatched,   setPoDispatched]   = useState(false);
    const [poDispatchDate, setPoDispatchDate] = useState(TODAY);
    const [poChallan,      setPoChallan]      = useState('');
    const [poDispatchQtys, setPoDispatchQtys] = useState({});

    const dateRef     = useRef();
    const skuRef      = useRef();
    const skuListRef  = useRef();
    const destRef     = useRef();
    const destTypeRef = useRef();
    const qtyRef      = useRef();
    const challanRef  = useRef();
    const mfgRef      = useRef();
    const expRef      = useRef();
    const byRef       = useRef();

    useEffect(() => { loadData(); }, []);
    useEffect(() => { setTimeout(() => dateRef.current?.focus(), 300); }, [mode]);

    async function loadData() {
        setLoading(true);
        try {
            const d = await fetch(`${API_BASE}/inventory-outward`).then(r => r.json());
            setSkus(d.skus                  || []);
            setDestTypes(d.destination_types || []);
            setDestinations(d.destinations   || []);
            setRecent(d.recent               || []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    }

    // ── Bulk helpers ─────────────────────────────────────────────────────────
    function handleFixed(key, val) { setFixed(f => ({ ...f, [key]: val })); }

    function handleRow(idx, key, val) {
        setRows(rs => rs.map((r,i) => i===idx ? { ...r, [key]: val } : r));
    }

    function addRow() { setRows(rs => [...rs, { ...emptyRow }]); }

    function removeRow(idx) { setRows(rs => rs.filter((_,i) => i!==idx)); }

    function getFilteredSkus(search) {
        if (!search || search.length < 2) return [];
        return skus.filter(s =>
            s.product_name?.toLowerCase().includes(search.toLowerCase()) ||
            String(s.ean_number).includes(search)
        ).slice(0, 12);
    }

    function selectSkuForRow(idx, sku) {
        setRows(rs => rs.map((r,i) => i===idx ? {
            ...r,
            ean_number:   String(sku.ean_number),
            product_name: sku.product_name,
            skuSearch:    sku.product_name,
            skuDropdown:  false,
            skuHighlight: 0,
            skuPreview:   ''
        } : r));
    }

    function handleRowSkuKey(e, idx) {
        const row      = rows[idx];
        const filtered = getFilteredSkus(row.skuSearch);
        if (!row.skuDropdown || filtered.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = Math.min(row.skuHighlight + 1, filtered.length - 1);
            handleRow(idx, 'skuHighlight', next);
            handleRow(idx, 'skuPreview', filtered[next].product_name);
            setTimeout(() => {
                const dd = document.getElementById(`row-dd-${idx}`);
                if (dd) dd.children[next]?.scrollIntoView({ block: 'nearest' });
            }, 10);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = Math.max(row.skuHighlight - 1, 0);
            handleRow(idx, 'skuHighlight', prev);
            handleRow(idx, 'skuPreview', filtered[prev].product_name);
            setTimeout(() => {
                const dd = document.getElementById(`row-dd-${idx}`);
                if (dd) dd.children[prev]?.scrollIntoView({ block: 'nearest' });
            }, 10);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            selectSkuForRow(idx, filtered[row.skuHighlight]);
        } else if (e.key === 'Escape') {
            handleRow(idx, 'skuDropdown', false);
        }
    }

    async function handleBulkSave() {
        if (!fixed.destination)      { setMsg({ type:'error', text:'Please select destination' });      return; }
        if (!fixed.destination_type) { setMsg({ type:'error', text:'Please select destination type' }); return; }

        const validRows = rows.filter(r => r.ean_number && r.quantity && parseInt(r.quantity) > 0);
        if (validRows.length === 0) { setMsg({ type:'error', text:'Add at least one SKU with quantity' }); return; }

        setSubmitting(true); setMsg(null);
        try {
            const payload = editBatch
                ? { batch_id: editBatch, fixed, rows: validRows }
                : { fixed, rows: validRows };

            const res  = await fetch(`${API_BASE}/inventory-outward`, {
                method: editBatch ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type:'success', text: data.message });
                setFixed({ ...emptyFixed, updated_by: user?.username || '' });
                setRows([{ ...emptyRow }]);
                setEditBatch(null);
                await loadData();
                setTimeout(() => dateRef.current?.focus(), 100);
            } else {
                setMsg({ type:'error', text: typeof data === 'string' ? data : 'Failed to save' });
            }
        } catch(err) {
            setMsg({ type:'error', text: 'Network error: ' + err.message });
        } finally { setSubmitting(false); }
    }

    // ── Single helpers ───────────────────────────────────────────────────────
    function handleSingle(key, val) { setSingleForm(f => ({ ...f, [key]: val })); }

    function selectSku(sku) {
        setSelSku(sku);
        setSingleForm(f => ({ ...f, ean_number: String(sku.ean_number), product_name: sku.product_name }));
        setSkuSearch(sku.product_name);
        setSkuDropdown(false); setSkuHighlight(0); setSkuPreview('');
        setTimeout(() => destRef.current?.focus(), 50);
    }

    function handleSkuKey(e) {
        const filtered = getFilteredSkus(skuSearch);
        if (!skuDropdown || filtered.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = Math.min(skuHighlight + 1, filtered.length - 1);
            setSkuHighlight(next); setSkuPreview(filtered[next].product_name);
            skuListRef.current?.children[next]?.scrollIntoView({ block:'nearest' });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = Math.max(skuHighlight - 1, 0);
            setSkuHighlight(prev); setSkuPreview(filtered[prev].product_name);
            skuListRef.current?.children[prev]?.scrollIntoView({ block:'nearest' });
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault(); selectSku(filtered[skuHighlight]); setSkuPreview('');
        } else if (e.key === 'Escape') { setSkuDropdown(false); setSkuPreview(''); }
    }

    function handleSelectKey(e, nextRef) {
        if (e.key === 'Enter') { e.preventDefault(); nextRef?.current?.focus(); }
    }

    function startSingleEdit(row) {
        setEditKey(row.update_time_key);
        setSingleForm({
            outward_date:           row.outward_date           || TODAY,
            invoice_challan_number: row.invoice_challan_number || '',
            destination:            row.destination            || '',
            destination_type:       row.destination_type       || '',
            ean_number:             row.ean_number             || '',
            product_name:           row.product_name           || '',
            quantity:               row.quantity               || '',
            mfg_date:               row.mfg_date               || '',
            exp_date:               row.exp_date               || '',
            updated_by:             row.updated_by             || '',
        });
        setSkuSearch(skus.find(s => String(s.ean_number) === String(row.ean_number))?.product_name || row.product_name || row.ean_number);
        setMsg({ type:'info', text:`Editing entry from ${row.outward_date}` });
        window.scrollTo({ top:0, behavior:'smooth' });
        setTimeout(() => dateRef.current?.focus(), 400);
    }

    async function startBatchEdit(batch) {
        const res  = await fetch(`${API_BASE}/inventory-outward?batch_id=${batch.batch_id}`);
        const data = await res.json();
        setFixed({
            outward_date:           batch.outward_date           || TODAY,
            invoice_challan_number: batch.invoice_challan_number || '',
            destination:            batch.destination            || '',
            destination_type:       batch.destination_type       || '',
            updated_by:             batch.updated_by             || '',
        });
        setRows(data.map(r => ({
            ean_number:   r.ean_number,
            product_name: skus.find(s => String(s.ean_number) === String(r.ean_number))?.product_name || r.product_name || r.ean_number,
            quantity:     r.quantity,
            mfg_date:     r.mfg_date || '',
            exp_date:     r.exp_date || '',
            skuSearch:    skus.find(s => String(s.ean_number) === String(r.ean_number))?.product_name || r.product_name || r.ean_number,
            skuDropdown:  false, skuHighlight:0, skuPreview:''
        })));
        setEditBatch(batch.batch_id);
        setMode('bulk');
        setMsg({ type:'info', text:`Editing batch — ${batch.sku_count} SKUs` });
        window.scrollTo({ top:0, behavior:'smooth' });
    }

    async function handleSingleSubmit() {
        if (!singleForm.ean_number)       { setMsg({ type:'error', text:'Please select a SKU' });            skuRef.current?.focus();      return; }
        if (!singleForm.destination)      { setMsg({ type:'error', text:'Please select destination' });      destRef.current?.focus();     return; }
        if (!singleForm.destination_type) { setMsg({ type:'error', text:'Please select destination type' }); destTypeRef.current?.focus(); return; }
        if (!singleForm.quantity || parseInt(singleForm.quantity) <= 0) {
            setMsg({ type:'error', text:'Enter valid quantity' }); qtyRef.current?.focus(); return;
        }

        setSubmitting(true); setMsg(null);
        try {
            const isEdit  = !!editKey;
            const payload = isEdit ? { ...singleForm, update_time_key: editKey } : singleForm;
            const res     = await fetch(`${API_BASE}/inventory-outward`, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type:'success', text: isEdit ? 'Entry updated' : 'Entry saved' });
                const savedName = singleForm.updated_by;
                setSingleForm({ ...emptyFixed, updated_by: savedName, ean_number:'', product_name:'', quantity:'', mfg_date:'', exp_date:'' });
                setSelSku(null); setSkuSearch(''); setSkuPreview(''); setEditKey(null);
                await loadData();
                setTimeout(() => dateRef.current?.focus(), 100);
            } else {
                setMsg({ type:'error', text: typeof data === 'string' ? data : 'Failed' });
            }
        } catch(err) {
            setMsg({ type:'error', text: 'Network error: ' + err.message });
        } finally { setSubmitting(false); }
    }

    async function handleDelete(key, batchId) {
        try {
            const url = batchId
                ? `${API_BASE}/inventory-outward?batch_id=${encodeURIComponent(batchId)}`
                : `${API_BASE}/inventory-outward?key=${encodeURIComponent(key)}`;
            const res  = await fetch(url, { method:'DELETE' });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type:'success', text:'Deleted' });
                setDeleteKey(null);
                await loadData();
            }
        } catch(err) { setMsg({ type:'error', text:'Delete failed' }); }
    }

    async function toggleBatchExpand(batch) {
        if (expandedBatch === batch.batch_id) { setExpandedBatch(null); return; }
        setExpandedBatch(batch.batch_id);
        if (!batchDetails[batch.batch_id]) {
            const res  = await fetch(`${API_BASE}/inventory-outward?batch_id=${batch.batch_id}`);
            const data = await res.json();
            setBatchDetails(d => ({ ...d, [batch.batch_id]: data }));
        }
    }

    // ── PO/SO helpers ────────────────────────────────────────────────────────
    async function lookupSO() {
        if (!poSearch.trim()) return;
        setPoLoading(true); setPoError(''); setPoData(null); setPoDispatched(false); setPoChecked({});
        try {
            const param = poSearchType === 'so'
                ? `so_number=${encodeURIComponent(poSearch.trim())}`
                : `po_number=${encodeURIComponent(poSearch.trim())}`;
            const res  = await fetch(`${API_BASE}/inventory-outward?${param}`);
            const data = await res.json();
            if (!res.ok || data.error) {
                setPoError(data.error || 'No record found for this number');
            } else {
                setPoData(data);
const checked = {};
data.lines.forEach(l => { checked[l.ean] = true; });
setPoChecked(checked);
setPoChallan(data.header.voucher_no);
// Initialize editable dispatch qtys
const qtys = {};
data.lines.forEach(l => {
    qtys[l.ean] = l.actual_qty !== null ? l.actual_qty : 0;
});
setPoDispatchQtys(qtys);
            }
        } catch(err) {
            setPoError('Network error — ' + err.message);
        } finally { setPoLoading(false); }
    }

    async function handlePoDispatch() {
        const selectedLines = poData.lines.filter(l => poChecked[l.ean] && parseFloat(poDispatchQtys[l.ean] || 0) > 0);
if (selectedLines.length === 0) { setPoError('Select at least one SKU with qty greater than 0'); return; }
        setSubmitting(true); setPoError('');
        try {
            const payload = {
                fixed: {
                    outward_date:           poDispatchDate,
                    invoice_challan_number: poChallan || poData.header.voucher_no,
                    destination:            poData.header.buyer,
                    destination_type:       'Platform Dispatch',
                    updated_by:             user?.username || 'Portal User',
                },
                rows: selectedLines.map(l => ({
    ean_number:   l.ean,
    product_name: l.sku_name,
    quantity:     parseFloat(poDispatchQtys[l.ean] || 0),
}))
            };
            const res  = await fetch(`${API_BASE}/inventory-outward`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setPoDispatched(true);
                await loadData();
            } else {
                setPoError('Failed to save dispatch');
            }
        } catch(err) {
            setPoError('Network error — ' + err.message);
        } finally { setSubmitting(false); }
    }

    const validRowCount = rows.filter(r => r.ean_number && r.quantity).length;

    function handleGlobalKey(e) {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            if (mode === 'bulk')   handleBulkSave();
            if (mode === 'single') handleSingleSubmit();
        }
    }

    return (
        <div onKeyDown={handleGlobalKey}>
            <div className="page-header">
                <h1>Outward Entry</h1>
            </div>

            {/* Mode toggle */}
            <div style={{
                display:'inline-flex', background:'#F1F5F9',
                borderRadius:'10px', padding:'4px', marginBottom:'20px',
                border:'1px solid #E2E8F0'
            }}>
                {['single','bulk','po'].map(m => (
                    <button key={m}
                        onClick={() => { setMode(m); setMsg(null); setEditKey(null); setEditBatch(null); }}
                        style={{
                            padding:'8px 24px', borderRadius:'7px', border:'none',
                            background: mode===m ? '#0F1F2E' : 'transparent',
                            color: mode===m ? '#fff' : '#64748B',
                            fontWeight: mode===m ? 700 : 500,
                            fontSize:'15px', cursor:'pointer', transition:'all 0.15s'
                        }}>
                        {m === 'single' ? 'Single Entry' : m === 'bulk' ? 'Bulk Entry' : 'PO / SO Dispatch'}
                    </button>
                ))}
            </div>

            {/* ── SINGLE MODE ── */}
            {mode === 'single' && (
                <div data-tutorial="outward-form" style={{
                    background:'#fff', borderRadius:'14px',
                    boxShadow: editKey ? '0 0 0 3px #F59E0B,0 2px 12px rgba(0,0,0,0.08)' : '0 2px 12px rgba(0,0,0,0.08)',
                    padding:'24px 28px', marginBottom:'20px'
                }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                        <h3 style={{ fontSize:'25px', fontWeight:700, color: editKey?'#D97706':'#0F1F2E' }}>
                            {editKey ? 'Edit Entry' : 'New Single Outward Entry'}
                        </h3>
                        {msg && <MsgBox msg={msg} />}
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:'14px', marginBottom:'14px' }}>
                        <Field label="Date *">
                            <input ref={dateRef} type="date" style={iStyle}
                                value={singleForm.outward_date}
                                onChange={e => handleSingle('outward_date', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'||e.key==='Tab'){e.preventDefault();skuRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="SKU / EAN *" hint={selSku ? `EAN: ${selSku.ean_number} · ${selSku.product || ''}` : ''}>
                            <div style={{ position:'relative' }}>
                                <input ref={skuRef} type="text" style={iStyle}
                                    placeholder={skuPreview || "Type product name or EAN..."}
                                    value={skuSearch}
                                    onChange={e => {
                                        setSkuSearch(e.target.value); setSkuDropdown(true);
                                        setSkuHighlight(0); setSkuPreview('');
                                        setSelSku(null); handleSingle('ean_number',''); handleSingle('product_name','');
                                    }}
                                    onFocus={() => skuSearch.length >= 2 && setSkuDropdown(true)}
                                    onBlur={() => setTimeout(() => { setSkuDropdown(false); setSkuPreview(''); }, 150)}
                                    onKeyDown={handleSkuKey}
                                />
                                <SkuDropdown
                                    listRef={skuListRef}
                                    items={getFilteredSkus(skuSearch)}
                                    show={skuDropdown}
                                    highlight={skuHighlight}
                                    onSelect={selectSku}
                                    color="#F59E0B"
                                    bg="#FFF7ED"
                                />
                            </div>
                        </Field>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px', marginBottom:'14px' }}>
                        <Field label="Destination *">
                            <select ref={destRef} style={iStyle} value={singleForm.destination}
                                onChange={e => handleSingle('destination', e.target.value)}
                                onKeyDown={e => handleSelectKey(e, destTypeRef)}>
                                <option value="">Select destination</option>
                                {destinations.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </Field>
                        <Field label="Destination Type *">
                            <select ref={destTypeRef} style={iStyle} value={singleForm.destination_type}
                                onChange={e => handleSingle('destination_type', e.target.value)}
                                onKeyDown={e => handleSelectKey(e, qtyRef)}>
                                <option value="">Select type</option>
                                {destTypes.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Quantity *">
                            <input ref={qtyRef} type="number" style={iStyle} min="1"
                                placeholder="Units" value={singleForm.quantity}
                                onChange={e => handleSingle('quantity', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();challanRef.current?.focus();}}}
                            />
                        </Field>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'14px', marginBottom:'20px' }}>
                        <Field label="Invoice / Challan No.">
                            <input ref={challanRef} type="text" style={iStyle} placeholder="Optional"
                                value={singleForm.invoice_challan_number}
                                onChange={e => handleSingle('invoice_challan_number', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();mfgRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="Mfg Date">
                            <input ref={mfgRef} type="date" style={iStyle}
                                value={singleForm.mfg_date}
                                onChange={e => handleSingle('mfg_date', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();expRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="Expiry Date">
                            <input ref={expRef} type="date" style={iStyle}
                                value={singleForm.exp_date}
                                onChange={e => handleSingle('exp_date', e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();byRef.current?.focus();}}}
                            />
                        </Field>
                        <Field label="Your Name">
                            <input ref={byRef} type="text"
                                style={{ ...iStyle, background:'#F1F5F9', color:'#64748B' }}
                                value={singleForm.updated_by}
                                readOnly
                            />
                        </Field>
                    </div>

                    <ActionRow
                        submitting={submitting} isEdit={!!editKey}
                        onSave={() => { if (!hasEditAccess(user, 'inventory', 'outward')) return notifyEditBlocked(); handleSingleSubmit(); }}
                        onClear={() => {
                            if (editKey) setEditKey(null);
                            const n = singleForm.updated_by;
                            setSingleForm({ ...emptyFixed, updated_by:n, ean_number:'', product_name:'', quantity:'', mfg_date:'', exp_date:'' });
                            setSelSku(null); setSkuSearch(''); setSkuPreview(''); setMsg(null);
                            dateRef.current?.focus();
                        }}
                        saveLabel={editKey ? 'Update Entry' : 'Save Entry'}
                        color="linear-gradient(135deg,#1A1A1A,#3D3D3D)"
                    />
                </div>
            )}

            {/* ── BULK MODE ── */}
            {mode === 'bulk' && (
                <div style={{
                    background:'#fff', borderRadius:'14px',
                    boxShadow: editBatch ? '0 0 0 3px #F59E0B,0 2px 12px rgba(0,0,0,0.08)' : '0 2px 12px rgba(0,0,0,0.08)',
                    padding:'24px 28px', marginBottom:'20px'
                }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                        <h3 style={{ fontSize:'15px', fontWeight:700, color: editBatch?'#D97706':'#0F1F2E' }}>
                            {editBatch ? 'Edit Batch' : 'Bulk Outward Entry'}
                        </h3>
                        {msg && <MsgBox msg={msg} />}
                    </div>

                    {/* Fixed fields */}
                    <div style={{
                        background:'#FFF7ED', borderRadius:'10px',
                        padding:'16px 20px', marginBottom:'20px',
                        border:'1px solid #FDE68A'
                    }}>
                        <div style={{ fontSize:'12px', fontWeight:700, color:'#000000', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'12px' }}>
                            Common Fields (apply to all SKUs)
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                            <Field label="Date *">
                                <input ref={dateRef} type="date" style={iStyle}
                                    value={fixed.outward_date}
                                    onChange={e => handleFixed('outward_date', e.target.value)} />
                            </Field>
                            <Field label="Destination *">
                                <select style={iStyle} value={fixed.destination}
                                    onChange={e => handleFixed('destination', e.target.value)}>
                                    <option value="">Select</option>
                                    {destinations.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </Field>
                            <Field label="Destination Type *">
                                <select style={iStyle} value={fixed.destination_type}
                                    onChange={e => handleFixed('destination_type', e.target.value)}>
                                    <option value="">Select</option>
                                    {destTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </Field>
                            <Field label="Your Name">
                                <input type="text"
                                    style={{ ...iStyle, background:'#F1F5F9', color:'#64748B' }}
                                    value={fixed.updated_by}
                                    readOnly />
                            </Field>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 3fr', gap:'12px' }}>
                            <Field label="Invoice / Challan">
                                <input type="text" style={iStyle} placeholder="Optional"
                                    value={fixed.invoice_challan_number}
                                    onChange={e => handleFixed('invoice_challan_number', e.target.value)} />
                            </Field>
                        </div>
                    </div>

                    {/* SKU rows */}
                    <div style={{ marginBottom:'16px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                            <div style={{ fontSize:'18px', fontWeight:700, color:'#0F1F2E' }}>
                                SKU List
                                <span style={{ marginLeft:'8px', fontSize:'14px', color:'#740bf5', fontWeight:600 }}>
                                    {validRowCount} ready
                                </span>
                            </div>
                        </div>

                        <div style={{ overflowX:'auto', overflowY:'visible' }}>
                            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'700px', overflow:'visible' }}>
                                <thead>
                                    <tr>
                                        {['#','SKU / Product Name','EAN','Qty *','Mfg Date','Exp Date','Action'].map(h => (
                                            <th key={h} style={{
                                                background:'#0F1F2E', color:'#fff',
                                                padding:'9px 12px', textAlign:'left',
                                                fontSize:'15px', fontWeight:600, whiteSpace:'nowrap'
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, idx) => {
                                        const filtered = getFilteredSkus(row.skuSearch);
                                        return (
                                            <tr key={idx} style={{ background: idx%2===0?'#fff':'#F8FAFC' }}>
                                                <td style={{ ...rtd, width:'40px', color:'#94A3B8', fontWeight:600 }}>{idx+1}</td>
                                                <td style={{ ...rtd, minWidth:'260px', position:'relative' }}>
                                                    <input type="text" style={{ ...iStyle, fontSize:'18px' }}
                                                        id={`sku-input-${idx}`}
                                                        placeholder={row.skuPreview || "Search SKU..."}
                                                        value={row.skuSearch}
                                                        onChange={e => {
                                                            handleRow(idx,'skuSearch',e.target.value);
                                                            handleRow(idx,'skuDropdown',true);
                                                            handleRow(idx,'skuHighlight',0);
                                                            handleRow(idx,'skuPreview','');
                                                            handleRow(idx,'ean_number','');
                                                        }}
                                                        onFocus={() => row.skuSearch.length>=2 && handleRow(idx,'skuDropdown',true)}
                                                        onBlur={() => setTimeout(()=>handleRow(idx,'skuDropdown',false),150)}
                                                        onKeyDown={e => handleRowSkuKey(e,idx)}
                                                    />
                                                    {row.skuDropdown && filtered.length > 0 && (
                                                        <RowSkuDropdown
                                                            idx={idx}
                                                            filtered={filtered}
                                                            highlight={row.skuHighlight}
                                                            onSelect={selectSkuForRow}
                                                        />
                                                    )}
                                                </td>
                                                <td style={{ ...rtd, fontSize:'15px', color:'#64748B', width:'130px' }}>
                                                    {row.ean_number || '—'}
                                                </td>
                                                <td style={{ ...rtd, width:'140px' }}>
                                                    <input type="number" min="1"
                                                        style={{ ...iStyle, fontSize:'17px' }}
                                                        placeholder="Qty"
                                                        value={row.quantity}
                                                        onChange={e => handleRow(idx,'quantity',e.target.value)}
                                                    />
                                                </td>
                                                <td style={{ ...rtd, width:'130px' }}>
                                                    <input type="date" style={{ ...iStyle, fontSize:'18px' }}
                                                        value={row.mfg_date}
                                                        onChange={e => handleRow(idx,'mfg_date',e.target.value)} />
                                                </td>
                                                <td style={{ ...rtd, width:'130px' }}>
                                                    <input type="date" style={{ ...iStyle, fontSize:'18px' }}
                                                        value={row.exp_date}
                                                        onChange={e => handleRow(idx,'exp_date',e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                addRow();
                                                                setTimeout(() => {
                                                                    const inputs = document.querySelectorAll('tbody tr td input[type="text"]');
                                                                    inputs[inputs.length - 1]?.focus();
                                                                }, 50);
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td style={{ ...rtd, width:'80px', textAlign:'center', whiteSpace:'nowrap' }}>
                                                    {idx === rows.length - 1 && (
                                                        <button onClick={addRow} style={{
                                                            background:'#F1F5F9', color:'#0F1F2E',
                                                            border:'1.5px solid #CBD5E1', borderRadius:'5px',
                                                            width:'28px', height:'28px', cursor:'pointer',
                                                            fontWeight:700, fontSize:'16px', marginRight:'4px'
                                                        }}>+</button>
                                                    )}
                                                    {rows.length > 1 && (
                                                        <button onClick={() => removeRow(idx)} style={{
                                                            background:'#FEE2E2', color:'#991B1B',
                                                            border:'none', borderRadius:'5px',
                                                            width:'28px', height:'28px', cursor:'pointer',
                                                            fontWeight:700, fontSize:'14px'
                                                        }}>x</button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <ActionRow
                        submitting={submitting} isEdit={!!editBatch}
                        onSave={() => { if (!hasEditAccess(user, 'inventory', 'outward')) return notifyEditBlocked(); handleBulkSave(); }}
                        onClear={() => {
                            setFixed({ ...emptyFixed, updated_by: user?.username || '' });
                            setRows([{...emptyRow}]);
                            setEditBatch(null); setMsg(null);
                            dateRef.current?.focus();
                        }}
                        saveLabel={editBatch ? 'Update Batch' : `Save ${validRowCount} SKU${validRowCount!==1?'s':''}`}
                        color="linear-gradient(135deg,#1A1A1A,#3D3D3D)"
                    />
                </div>
            )}

            {/* ── PO / SO DISPATCH MODE ── */}
            {mode === 'po' && (
                <div style={{
                    background:'#fff', borderRadius:'14px',
                    boxShadow:'0 2px 12px rgba(0,0,0,0.08)',
                    padding:'24px 28px', marginBottom:'20px'
                }}>
                    <h3 style={{ fontSize:'25px', fontWeight:700, color:'#0F1F2E', marginBottom:'4px' }}>
                        PO / SO Dispatch
                    </h3>
                    <p style={{ fontSize:'14px', color:'#64748B', marginBottom:'24px', marginTop:'4px' }}>
                        Enter a Sales Order number or Buyer PO number to load SKUs and confirm dispatch
                    </p>

                    {/* Search bar */}
                    <div style={{ display:'flex', gap:'10px', marginBottom:'20px', alignItems:'flex-end', flexWrap:'wrap' }}>
                        <div style={{ display:'flex', borderRadius:'8px', overflow:'hidden', border:'1.5px solid #D1C4B8' }}>
                            <button
                                onClick={() => { setPoSearchType('so'); setPoData(null); setPoError(''); }}
                                style={{
                                    padding:'10px 20px', border:'none', cursor:'pointer',
                                    fontSize:'14px', fontWeight:600, transition:'all 0.15s',
                                    background: poSearchType==='so' ? '#0F1F2E' : '#F8FAFC',
                                    color: poSearchType==='so' ? '#fff' : '#64748B',
                                }}>
                                SO Number
                            </button>
                            <button
                                onClick={() => { setPoSearchType('po'); setPoData(null); setPoError(''); }}
                                style={{
                                    padding:'10px 20px', border:'none', cursor:'pointer',
                                    fontSize:'14px', fontWeight:600, transition:'all 0.15s',
                                    background: poSearchType==='po' ? '#0F1F2E' : '#F8FAFC',
                                    color: poSearchType==='po' ? '#fff' : '#64748B',
                                }}>
                                PO Number
                            </button>
                        </div>
                        <input type="text" style={{ ...iStyle, maxWidth:'300px' }}
                            placeholder={poSearchType==='so' ? 'e.g. 641' : 'e.g. PO-A000328345'}
                            value={poSearch}
                            onChange={e => { setPoSearch(e.target.value); setPoError(''); }}
                            onKeyDown={e => { if(e.key==='Enter') lookupSO(); }}
                        />
                        <button onClick={lookupSO} disabled={poLoading} style={{
                            padding:'11px 28px',
                            background: poLoading ? '#94A3B8' : 'linear-gradient(135deg,#1A1A1A,#3D3D3D)',
                            color:'#fff', border:'none', borderRadius:'8px',
                            fontSize:'15px', fontWeight:700,
                            cursor: poLoading ? 'not-allowed' : 'pointer',
                            boxShadow:'0 2px 6px rgba(0,0,0,0.2)'
                        }}>
                            {poLoading ? 'Loading...' : 'Fetch'}
                        </button>
                    </div>

                    {/* Error */}
                    {poError && (
                        <div style={{
                            padding:'10px 16px', background:'#FEE2E2', color:'#991B1B',
                            borderRadius:'8px', marginBottom:'16px',
                            fontSize:'14px', fontWeight:600
                        }}>
                            {poError}
                        </div>
                    )}

                    {/* SO data */}
                    {poData && !poDispatched && (
                        <>
                            {/* Header */}
                            <div style={{
                                background:'#F8FAFC', borderRadius:'10px',
                                padding:'16px 20px', marginBottom:'20px',
                                border:'1px solid #E2E8F0'
                            }}>
                                <div style={{ fontSize:'11px', fontWeight:700, color:'#94A3B8', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'12px' }}>
                                    Order Details
                                </div>
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px' }}>
                                    {[
                                        ['SO Number',    poData.header.voucher_no],
                                        ['SO Date',      poData.header.so_date],
                                        ['Buyer',        poData.header.buyer],
                                        ['PO Reference', poData.header.order_ref_no || '—'],
                                        ['Salesman',     poData.header.salesman     || '—'],
                                        ['Total Qty',    poData.header.total_qty],
                                        ['Gross Total',  'Rs. ' + Number(poData.header.gross_total).toLocaleString('en-IN')],
                                    ].map(([label, val]) => (
                                        <div key={label}>
                                            <div style={{ fontSize:'11px', color:'#94A3B8', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px' }}>{label}</div>
                                            <div style={{ fontSize:'15px', color:'#1A1A1A', fontWeight:600, marginTop:'4px' }}>{val}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* SKU table */}
                            <div style={{ marginBottom:'20px' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                                    <div style={{ fontSize:'15px', fontWeight:700, color:'#0F1F2E' }}>
                                        SKUs to Dispatch
                                        <span style={{ marginLeft:'8px', fontSize:'13px', color:'#8B5E3C', fontWeight:600 }}>
                                            {Object.values(poChecked).filter(Boolean).length} of {poData.lines.length} selected
                                        </span>
                                    </div>
                                    <button onClick={() => {
                                        const allChecked = poData.lines.every(l => poChecked[l.ean]);
                                        const next = {};
                                        poData.lines.forEach(l => { next[l.ean] = !allChecked; });
                                        setPoChecked(next);
                                    }} style={{
                                        padding:'6px 16px', background:'#F1F5F9', color:'#475569',
                                        border:'1.5px solid #E2E8F0', borderRadius:'6px',
                                        fontSize:'13px', fontWeight:600, cursor:'pointer'
                                    }}>
                                        {poData.lines.every(l => poChecked[l.ean]) ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>

                                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                                    <thead>
                                        <tr>
                                           {['','EAN','SKU Name','PO Qty','Actual Qty','Dispatch Qty'].map(h => (
                                                <th key={h} style={{
                                                    background:'#0F1F2E', color:'#fff',
                                                    padding:'9px 12px', textAlign:'left',
                                                    fontSize:'14px', fontWeight:600, whiteSpace:'nowrap'
                                                }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {poData.lines.map((l, i) => (
                                            <tr key={l.ean} style={{
                                                background: i%2===0 ? '#fff' : '#F8FAFC',
                                                opacity: poChecked[l.ean] ? 1 : 0.45
                                            }}>
                                                <td style={{ ...rtd, width:'48px', textAlign:'center' }}>
                                                    <input type="checkbox"
                                                        checked={!!poChecked[l.ean]}
                                                        onChange={e => setPoChecked(c => ({ ...c, [l.ean]: e.target.checked }))}
                                                        style={{ width:'16px', height:'16px', cursor:'pointer', accentColor:'#0F1F2E' }}
                                                    />
                                                </td>
                                                <td style={{ ...rtd, fontSize:'13px', color:'#94A3B8' }}>{l.ean}</td>
                                                <td style={{ ...rtd, fontSize:'15px', fontWeight: poChecked[l.ean] ? 600 : 400, color:'#1A1A1A' }}>{l.sku_name}</td>
                                                <td style={{ ...rtd, fontSize:'14px', color:'#94A3B8' }}>{l.po_qty}</td>
<td style={{ ...rtd, fontSize:'15px', fontWeight:700, color:'#0F1F2E' }}>
    {l.actual_qty ?? l.po_qty}
</td>
<td style={{ ...rtd, width:'110px' }}>
    <input type="number" min="0"
        style={{ ...iStyle, fontSize:'14px', width:'90px',
            background: poChecked[l.ean] ? '#F8FAFC' : '#F1F5F9',
            color: (poDispatchQtys[l.ean] === 0 || poDispatchQtys[l.ean] === '0') ? '#EF4444' : '#1E293B'
        }}
        value={poDispatchQtys[l.ean] ?? 0}
        disabled={!poChecked[l.ean]}
        onChange={e => setPoDispatchQtys(q => ({ ...q, [l.ean]: e.target.value }))}
    />
</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Dispatch details */}
                            <div style={{
                                background:'#F8FAFC', borderRadius:'10px',
                                padding:'16px 20px', marginBottom:'20px',
                                border:'1px solid #E2E8F0'
                            }}>
                                <div style={{ fontSize:'11px', fontWeight:700, color:'#94A3B8', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'12px' }}>
                                    Dispatch Details
                                </div>
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px' }}>
                                    <Field label="Dispatch Date *">
                                        <input type="date" style={iStyle}
                                            value={poDispatchDate}
                                            onChange={e => setPoDispatchDate(e.target.value)} />
                                    </Field>
                                    <Field label="Challan / Invoice No.">
                                        <input type="text" style={iStyle}
                                            placeholder="Leave blank to use SO number"
                                            value={poChallan}
                                            onChange={e => setPoChallan(e.target.value)} />
                                    </Field>
                                    <Field label="Dispatched By">
                                        <input type="text"
                                            style={{ ...iStyle, background:'#F1F5F9', color:'#64748B' }}
                                            value={user?.username || ''}
                                            readOnly />
                                    </Field>
                                </div>
                            </div>

                            <button onClick={handlePoDispatch} disabled={submitting} style={{
                                padding:'12px 36px',
                                background: submitting ? '#94A3B8' : 'linear-gradient(135deg,#1A1A1A,#3D3D3D)',
                                color:'#fff', border:'none', borderRadius:'8px',
                                fontSize:'15px', fontWeight:700,
                                cursor: submitting ? 'not-allowed' : 'pointer',
                                boxShadow:'0 2px 6px rgba(0,0,0,0.2)'
                            }}>
                                {submitting
                                    ? 'Saving...'
                                    : `Confirm Dispatch — ${Object.values(poChecked).filter(Boolean).length} SKU${Object.values(poChecked).filter(Boolean).length !== 1 ? 's' : ''}`
                                }
                            </button>
                        </>
                    )}

                    {/* Success */}
                    {poDispatched && poData && (
                        <div style={{
                            padding:'32px', background:'#F0FDF4', borderRadius:'12px',
                            border:'2px solid #86EFAC', textAlign:'center'
                        }}>
                            <div style={{ fontSize:'16px', fontWeight:700, color:'#065F46', marginBottom:'6px' }}>
                                Dispatch Recorded Successfully
                            </div>
                            <div style={{ fontSize:'14px', color:'#047857', marginBottom:'4px' }}>
                                SO {poData.header.voucher_no} — {poData.header.buyer}
                            </div>
                            <div style={{ fontSize:'13px', color:'#6B7280', marginBottom:'20px' }}>
                                {Object.values(poChecked).filter(Boolean).length} SKUs dispatched on {poDispatchDate}
                            </div>
                            <button onClick={() => {
                                setPoData(null); setPoSearch(''); setPoDispatched(false);
                                setPoChecked({}); setPoError(''); setPoChallan(''); setPoDispatchDate(TODAY); setPoDispatchQtys({});
                            }} style={{
                                padding:'10px 28px',
                                background:'linear-gradient(135deg,#1A1A1A,#3D3D3D)',
                                color:'#fff', border:'none', borderRadius:'8px',
                                fontSize:'14px', fontWeight:600, cursor:'pointer'
                            }}>
                                Record Another Dispatch
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Delete modal */}
            {deleteKey && (
                <div style={{
                    position:'fixed',top:0,left:0,right:0,bottom:0,
                    background:'rgba(0,0,0,0.5)',zIndex:500,
                    display:'flex',alignItems:'center',justifyContent:'center'
                }}>
                    <div style={{
                        background:'#fff',borderRadius:'14px',padding:'28px 32px',
                        maxWidth:'400px',width:'90%',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'
                    }}>
                        <h3 style={{ fontSize:'16px',fontWeight:700,color:'#0F1F2E',marginBottom:'10px' }}>Delete Entry?</h3>
                        <p style={{ fontSize:'13px',color:'#64748B',marginBottom:'20px' }}>
                            {deleteKey.batch_id ? `This will delete all ${deleteKey.sku_count} SKUs in this batch.` : 'This entry will be permanently deleted.'}
                        </p>
                        <div style={{ display:'flex',gap:'10px' }}>
                            <button onClick={() => { if (!hasEditAccess(user, 'inventory', 'outward')) return notifyEditBlocked(); handleDelete(deleteKey.key, deleteKey.batch_id); }} style={{
                                padding:'10px 24px',background:'#EF4444',color:'#fff',
                                border:'none',borderRadius:'8px',fontSize:'14px',fontWeight:700,cursor:'pointer'
                            }}>Yes, Delete</button>
                            <button onClick={() => setDeleteKey(null)} style={{
                                padding:'10px 20px',background:'#F1F5F9',color:'#475569',
                                border:'1.5px solid #E2E8F0',borderRadius:'8px',fontSize:'14px',fontWeight:600,cursor:'pointer'
                            }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent entries */}
            <div style={{ background:'#fff',borderRadius:'14px',boxShadow:'0 2px 8px rgba(0,0,0,0.07)',overflow:'hidden' }}>
                <div style={{ padding:'14px 20px',borderBottom:'1px solid #F1F5F9',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                    <h3 style={{ fontSize:'15px',fontWeight:700,color:'#0F1F2E' }}>Recent Outward Entries</h3>
                    <span style={{ fontSize:'12px',color:'#94A3B8' }}>{recent.length} entries</span>
                </div>
                <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%',borderCollapse:'collapse',fontSize:'13px' }}>
                        <thead>
                            <tr>
                                {['Actions','Date','Challan','Destination','Type','SKUs','Total Qty','By','Time'].map(h => (
                                    <th key={h} style={{
                                        background:'#0F1F2E',color:'#fff',padding:'9px 12px',
                                        textAlign:'left',fontSize:'11.5px',fontWeight:600,whiteSpace:'nowrap'
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {recent.length === 0 ? (
                                <tr><td colSpan={9} style={{ padding:'30px',textAlign:'center',color:'#94A3B8' }}>No entries yet</td></tr>
                            ) : recent.map((r,i) => (
                                <>
                                    <tr key={i} style={{ background: i%2===0?'#fff':'#F8FAFC' }}>
                                        <td style={{ ...rtd, whiteSpace:'nowrap' }}>
                                            <button onClick={() => { if (!hasEditAccess(user, 'inventory', 'outward')) return notifyEditBlocked(); r.batch_id ? startBatchEdit(r) : startSingleEdit(r); }}
                                                style={{ padding:'4px 8px',background:'#EFF6FF',color:'#1D4ED8',border:'1px solid #BFDBFE',borderRadius:'5px',fontSize:'11px',fontWeight:600,cursor:'pointer',marginRight:'4px' }}>
                                                Edit
                                            </button>
                                            <button onClick={() => setDeleteKey({ key: r.update_time_key, batch_id: r.batch_id, sku_count: r.sku_count })}
                                                style={{ padding:'4px 8px',background:'#FEE2E2',color:'#991B1B',border:'1px solid #FECACA',borderRadius:'5px',fontSize:'11px',fontWeight:600,cursor:'pointer',marginRight:'4px' }}>
                                                Del
                                            </button>
                                            {r.batch_id && (
                                                <button onClick={() => toggleBatchExpand(r)}
                                                    style={{ padding:'4px 8px',background:'#FFF7ED',color:'#92400E',border:'1px solid #FDE68A',borderRadius:'5px',fontSize:'11px',fontWeight:600,cursor:'pointer' }}>
                                                    {expandedBatch===r.batch_id?'Hide':'Show'}
                                                </button>
                                            )}
                                        </td>
                                        <td style={rtd}>{r.outward_date}</td>
                                        <td style={rtd}>{r.invoice_challan_number || '—'}</td>
                                        <td style={rtd}>{r.destination}</td>
                                        <td style={rtd}>{r.destination_type}</td>
                                        <td style={{ ...rtd,fontWeight:700,color: r.batch_id?'#1D4ED8':'#334155' }}>
                                            {r.batch_id ? `${r.sku_count} SKUs` : '1 SKU'}
                                        </td>
                                        <td style={{ ...rtd,fontWeight:700,color:'#B45309' }}>{r.total_quantity}</td>
                                        <td style={rtd}>{r.updated_by}</td>
                                        <td style={{ ...rtd,fontSize:'11px',color:'#94A3B8' }}>{r.update_time}</td>
                                    </tr>
                                    {expandedBatch===r.batch_id && batchDetails[r.batch_id] && (
                                        batchDetails[r.batch_id].map((d,di) => (
                                            <tr key={`d-${di}`} style={{ background:'#FFF7ED' }}>
                                                <td style={rtd}></td>
                                                <td style={{ ...rtd,paddingLeft:'24px',color:'#92400E',fontSize:'12px' }}>-</td>
                                                <td colSpan={2} style={{ ...rtd,fontSize:'12px',color:'#92400E',fontWeight:600 }}>
                                                    {d.product_name || skus.find(s=>String(s.ean_number)===String(d.ean_number))?.product_name || d.ean_number}
                                                </td>
                                                <td style={{ ...rtd,fontSize:'11px',color:'#94A3B8' }}>{d.ean_number}</td>
                                                <td style={{ ...rtd,fontWeight:700,color:'#B45309' }}>Qty: {d.quantity}</td>
                                                <td style={rtd}>{d.mfg_date || '—'}</td>
                                                <td style={rtd}>{d.exp_date || '—'}</td>
                                                <td></td>
                                            </tr>
                                        ))
                                    )}
                                </>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ── Row SKU dropdown (position:fixed to escape table overflow) ────────────────
function RowSkuDropdown({ idx, filtered, highlight, onSelect }) {
    const el   = document.getElementById(`sku-input-${idx}`);
    const rect = el ? el.getBoundingClientRect() : null;
    if (!rect) return null;
    return (
        <div id={`row-dd-${idx}`} style={{
            position:'fixed',
            top:   rect.bottom + 2,
            left:  rect.left,
            width: rect.width,
            zIndex: 9999,
            background:'#fff', borderRadius:'8px',
            boxShadow:'0 8px 24px rgba(0,0,0,0.15)',
            border:'1px solid #E2E8F0',
            maxHeight:'220px', overflowY:'auto'
        }}>
            {filtered.map((s,i) => (
                <div key={s.ean_number}
                    onMouseDown={() => onSelect(idx, s)}
                    style={{
                        padding:'8px 12px', cursor:'pointer',
                        display:'flex', justifyContent:'space-between',
                        background: i===highlight ? '#FFF7ED' : '#fff',
                        borderLeft: i===highlight ? '3px solid #F59E0B' : '3px solid transparent',
                        borderBottom:'1px solid #F1F5F9', fontSize:'15px'
                    }}>
                    <span style={{ fontWeight: i===highlight?600:400 }}>{s.product_name}</span>
                    <span style={{ fontSize:'14px', color:'#94A3B8' }}>{s.ean_number}</span>
                </div>
            ))}
        </div>
    );
}

// ── Shared components ─────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
    return (
        <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <label style={{ fontSize:'15px', fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.6px' }}>{label}</label>
                {hint && <span style={{ fontSize:'11px', color:'#F59E0B', fontWeight:600 }}>{hint}</span>}
            </div>
            {children}
        </div>
    );
}

function MsgBox({ msg }) {
    return (
        <div style={{
            padding:'8px 16px', borderRadius:'8px',
            background: msg.type==='success'?'#D1FAE5':msg.type==='info'?'#FFF7ED':'#FEE2E2',
            color:      msg.type==='success'?'#065F46':msg.type==='info'?'#92400E':'#991B1B',
            fontWeight:600, fontSize:'13px'
        }}>{msg.text}</div>
    );
}

function SkuDropdown({ listRef, items, show, highlight, onSelect, color, bg }) {
    if (!show || items.length === 0) return null;
    return (
        <div ref={listRef} style={{
            position:'absolute', top:'100%', left:0, right:0, zIndex:100,
            background:'#fff', borderRadius:'8px',
            boxShadow:'0 8px 24px rgba(0,0,0,0.15)',
            border:'1px solid #E2E8F0', maxHeight:'240px', overflowY:'auto'
        }}>
            {items.map((s,i) => (
                <div key={s.ean_number} onMouseDown={() => onSelect(s)}
                    style={{
                        padding:'9px 14px', display:'flex', justifyContent:'space-between', alignItems:'center',
                        background: i===highlight ? bg : '#fff',
                        borderBottom:'1px solid #F1F5F9',
                        borderLeft: i===highlight ? `3px solid ${color}` : '3px solid transparent',
                        cursor:'pointer'
                    }}>
                    <span style={{ fontSize:'13px', color:'#1E293B', fontWeight: i===highlight?600:400 }}>{s.product_name}</span>
                    <span style={{ fontSize:'15px', color:'#94A3B8', marginLeft:'12px' }}>{s.ean_number}</span>
                </div>
            ))}
        </div>
    );
}

function ActionRow({ submitting, isEdit, onSave, onClear, saveLabel, color }) {
    return (
        <div style={{ display:'flex', gap:'12px', alignItems:'center' }}>
            <button onClick={onSave} disabled={submitting} style={{
                padding:'11px 28px',
                background: submitting ? '#94A3B8' : (color || 'linear-gradient(135deg,#1A1A1A,#3D3D3D)'),
                color:'#fff', border:'none', borderRadius:'8px',
                fontSize:'15px', fontWeight:700,
                cursor: submitting?'not-allowed':'pointer',
                boxShadow:'0 2px 6px rgba(0,0,0,0.2)'
            }}>
                {submitting ? 'Saving...' : saveLabel}
            </button>
            <button onClick={onClear} style={{
                padding:'11px 20px', background:'#F1F5F9', color:'#475569',
                border:'1.5px solid #E2E8F0', borderRadius:'8px',
                fontSize:'15px', fontWeight:600, cursor:'pointer'
            }}>
                {isEdit ? 'Cancel' : 'Clear'}
            </button>
        </div>
    );
}

const iStyle = {
    padding:'10px 12px', border:'1.5px solid #E2E8F0', borderRadius:'8px',
    fontSize:'16px', color:'#1E293B', background:'#F8FAFC',
    outline:'none', width:'100%', boxSizing:'border-box',
    transition:'border-color 0.15s, box-shadow 0.15s',
};

const rtd = { padding:'8px 12px', borderBottom:'1px solid #F1F5F9', color:'#334155', whiteSpace:'nowrap' };

